import { useState } from 'react';
import { isError, isNil } from 'lodash';
import {
    IWriteSmartReplyQuery,
    useWriteSmartReplyLazyQuery,
} from '@copilot/data/graphql/_generated';
import {
    CUSTOM_PROMPT_KEY,
    WriteSmartReplyErrors,
    WriteSmartReplyHook,
} from '@copilot/common/hooks/smartReply/smartReplyTypes';
import {
    getSmartReplyConversationContext,
    isSmartReply,
    isSmartReplyError,
} from '@copilot/common/hooks/smartReply/smartReplyUtils';
import { ErrorMap } from '../smartReplyConstants';
import { CommunicationStylesAnalysis } from '@copilot/common/types/communicationStyles';
import { useFeatureFlags } from '../../useFeatureFlags/useFeatureFlags';
/**
 * [Smart] Hook for writing a Smart Reply using the AI Chat Service
 * @param orgId
 * @param memberId
 * @param contactId
 */
export function useWriteSmartReply(
    orgId: string,
    memberId: string,
    contactId: string,
    communicationStyles?: CommunicationStylesAnalysis
): WriteSmartReplyHook {
    const [writeSmartReplyQuery, { data, refetch }] = useWriteSmartReplyLazyQuery({
        notifyOnNetworkStatusChange: true,
    });
    const [error, setError] = useState<Error | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const { isNewCommStyleSmartReplyEnabled } = useFeatureFlags();

    /**
     * Callback for writing a Smart Reply given a prompt
     * @param promptId
     * @param message
     */
    async function writeSmartReply(
        promptId: string,
        message?: string,
        includePersonalizedInsights?: boolean
    ) {
        const { sender, recipient, messageThread, connectionId } =
            await getSmartReplyConversationContext(orgId, memberId, contactId);

        return writeSmartReplyQuery({
            variables: {
                input: {
                    sender,
                    recipient,
                    messageThread,
                    recipientCommunicationStyles:
                        includePersonalizedInsights && isNewCommStyleSmartReplyEnabled
                            ? communicationStyles?.styles.map((style) => style.style)
                            : undefined,
                    prompt: {
                        // TODO - COPILOT-6513: Update to use promptId with no conditional.
                        promptId: isNil(message) ? promptId : CUSTOM_PROMPT_KEY,
                        message,
                    },
                    connectionId,
                },
            },
        });
    }

    const regenerateSmartReply = prepFetch(refetch);

    const callbacks = {
        writeSmartReply: prepFetch(writeSmartReply),
        regenerateSmartReply: () => regenerateSmartReply(),
    };

    if (loading) return [{ loading: true, data: undefined, error: undefined }, callbacks];

    if (error) return [{ loading: false, data: undefined, error: error }, callbacks];

    // Return the message if it can be parsed
    if (!isNil(data) && isSmartReply(data.writeSmartReply)) {
        return [{ loading, error: undefined, data: data?.writeSmartReply.message }, callbacks];
    }

    // Return an error if no previous condition was met
    return [
        { loading, data: undefined, error: new Error(WriteSmartReplyErrors.unexpectedFormat) },
        callbacks,
    ];

    /**
     * Wrap fetches with state management
     * @param fetchFn The function we want to wrap
     */
    function prepFetch<T extends (...args: any[]) => Promise<{ data?: IWriteSmartReplyQuery }>>(
        fetchFn: T
    ) {
        return async function (...args: Parameters<T>) {
            setLoading(true);
            setError(undefined);
            try {
                const result = await fetchFn(...args);
                if (!isNil(result?.data) && isSmartReplyError(result?.data.writeSmartReply)) {
                    const errorMessage =
                        ErrorMap[result.data.writeSmartReply.type] ??
                        WriteSmartReplyErrors.generation;
                    throw new Error(errorMessage);
                }
            } catch (err: unknown) {
                if (isError(err)) setError(err);
                else throw err;
            } finally {
                setLoading(false);
            }
        };
    }
}
